import { getWorkspaceFrameStyles } from '@components/v2/constants';
import type { CSSObject } from '@emotion/react';
import { alpha } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import type { PanelStylesParams, StyleParams } from './rachelPanel.types';

export const RACHEL_PANEL_WIDTH = 308;

const OVERLAY_PANEL_WIDTH = 390;
const OVERLAY_INSET = 0.5;
const SCRIM_OPACITY = 0.3;

const TRANSITIONED_OVERLAY_PROPERTIES = [
  'width',
  'background-color',
  'border-color',
  'border-radius',
];

const REDUCED_MOTION_QUERY = '@media (prefers-reduced-motion: reduce)';

const getFlowPanelStyles = ({ theme, isOpen, duration }: PanelStylesParams): CSSObject => ({
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  flexShrink: 0,
  position: 'relative',
  flexGrow: 0,
  flexBasis: isOpen ? RACHEL_PANEL_WIDTH : 0,
  transition: theme.transitions.create('flex-basis', {
    duration,
    easing: theme.transitions.easing.easeOut,
  }),
  [REDUCED_MOTION_QUERY]: { transition: 'none' },
});

const getOverlayPanelStyles = ({ theme, isOpen, duration }: PanelStylesParams): CSSObject => {
  const inset = theme.spacing(OVERLAY_INSET);
  const maxWidth = `calc(100% - ${theme.spacing(OVERLAY_INSET * 2)})`;

  return {
    position: 'fixed',
    boxSizing: 'border-box',
    top: inset,
    right: inset,
    bottom: inset,
    width: isOpen ? `min(${OVERLAY_PANEL_WIDTH}px, ${maxWidth})` : 0,
    zIndex: theme.zIndex.drawer + 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    overflow: 'hidden',
    pointerEvents: isOpen ? 'auto' : 'none',
    backgroundColor: 'transparent',
    border: '1px solid transparent',
    borderRadius: 0,
    transition: theme.transitions.create(TRANSITIONED_OVERLAY_PROPERTIES, {
      duration,
      easing: theme.transitions.easing.easeOut,
    }),
    [REDUCED_MOTION_QUERY]: { transition: 'none' },
    ...(isOpen
      ? { ...getWorkspaceFrameStyles(theme), borderRadius: theme.shape.borderRadiusLg }
      : {}),
  };
};

export const useRachelPanelStyles = makeStyles<StyleParams>()((theme, { isOpen }) => {
  const duration = theme.transitions.duration.shorter;

  return {
    panel: getFlowPanelStyles({ theme, isOpen, duration }),
    scrim: {
      position: 'fixed',
      inset: 0,
      backgroundColor: alpha(theme.palette.common.black, SCRIM_OPACITY),
      zIndex: theme.zIndex.drawer,
    },
    overlayPanel: getOverlayPanelStyles({ theme, isOpen, duration }),
    content: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
    },
  };
});
